interface Props { cs: boolean }

function CsBody() {
  return (
    <>
      <p>
        Cue sama o sobě je jen pojmenovaný slot. Veškerá "práce" se děje v payloadech. ShowX podporuje osm payload typů
        — každý pro jinou třídu hardwaru nebo integrace.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Přehled payload typů</h3>
      <div className="my-4 space-y-3 text-sm">
        {[
          {
            tag: 'lx_ref',
            title: 'Lighting console reference',
            cs: 'Nejčastější LX payload. Předá číslo cue světelnému pultu (driver eos = ETC Eos formát). Routing: lx_ref → device s driver=eos. Výstup: /eos/cue/{cuelist}/{number}/fire přes OSC.',
          },
          {
            tag: 'osc',
            title: 'Open Sound Control',
            cs: 'UDP paket na device host:port. Payload obsahuje OSC cestu a argumenty. Univerzální — pro QLab, MA2/3, Disguise, GrandMA, vlastní OSC zařízení.',
          },
          {
            tag: 'midi',
            title: 'MIDI',
            cs: 'Note on/off nebo Control Change na MIDI portu. Číslo kanálu 1–16, číslo noty/CC 0–127, hodnota 0–127. Pro hardware MIDI zařízení a starší konzole.',
          },
          {
            tag: 'msc',
            title: 'MIDI Show Control',
            cs: 'Standardizovaný show-control protokol přes MIDI. Příkazy go/stop/resume/load s číslem cue. Pro QLab (MSC mode), Eos (MSC mode), MA2.',
          },
          {
            tag: 'dmx',
            title: 'DMX přímý výstup',
            cs: 'Nastaví hodnotu jednoho DMX kanálu (0–255) na daném univerzu. Přes Art-Net (UDP port 6454) nebo sACN (UDP port 5568). Pro jednoduché LED/dimmer bez konzole.',
          },
          {
            tag: 'webhook',
            title: 'HTTP Webhook',
            cs: 'POST request na zadanou URL. Tělo je JSON objekt. Pro systémy s REST API — media servery, vlastní řídící aplikace, IoT brány.',
          },
          {
            tag: 'wait',
            title: 'Čekání (pauza)',
            cs: 'Vloží pauzu v milisekundách. Používá se uvnitř group payloadu pro sekvenční spouštění s časovými rozestupy.',
          },
          {
            tag: 'group',
            title: 'Skupina payloadů',
            cs: 'Vnořená sada payloadů spouštěná jako jeden celek. Umožňuje kombinovat typy a vkládat wait pauzy pro složitější timing.',
          },
        ].map(({ tag, title, cs }) => (
          <div key={tag} className="rounded border border-rule/40 p-4">
            <div className="flex gap-3 items-start">
              <code className="text-accent-deep font-bold w-20 shrink-0 font-mono">{tag}</code>
              <div>
                <div className="font-semibold text-ink">{title}</div>
                <div className="text-muted mt-1 leading-relaxed">{cs}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <h3 className="display-serif text-xl mt-10 mb-3">Hodnoty a jednotky</h3>
      <ul>
        <li>
          <strong>DMX hodnota</strong>: 0–255 (8-bit). 0 = vypnuto, 255 = plný výkon / plná intenzita.
        </li>
        <li>
          <strong>MIDI hodnota (velocity / CC value)</strong>: 0–127 (7-bit). 127 = maximum.
        </li>
        <li>
          <strong>MIDI kanál</strong>: 1–16.
        </li>
        <li>
          <strong>OSC čísla</strong>: float32 nebo int32, záleží na cílovém zařízení — QLab preferuje int32, Eos float.
        </li>
        <li>
          <strong>DMX universe</strong>: 0–32767 (Art-Net) nebo 1–63999 (sACN).
        </li>
      </ul>

      <h3 className="display-serif text-xl mt-10 mb-3">Routing: třívrstvý model</h3>
      <p>
        Payload nezná přímo IP adresu. Třívrstvý routing to odděluje:
      </p>
      <ol>
        <li>
          <strong>Payload</strong> — záměr v jazyce show: "tato cue chce jít na LX konzoli" (lx_ref) nebo "pošli toto
          na SX" (tag=SX).
        </li>
        <li>
          <strong>Routing pravidlo</strong> — match (<code>payload_type</code> nebo <code>tag_pattern</code>) →
          <code>target_device_id</code>. Nastavuje se v záložce Routing v shellu.
        </li>
        <li>
          <strong>Device</strong> — konkrétní zařízení: IP, port, driver. Nastavuje se v záložce Devices.
        </li>
      </ol>
      <p>
        Pokud vyměníš LX konzoli za jiný model, změníš jen driver a IP — cues zůstanou beze změny.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Příklad: multi-department cue</h3>
      <p>
        Ukázka reálné cue pro velký otevřecí moment show:
      </p>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-1">
          <div className="py-3 border-b border-rule/40">
            <div className="text-ink font-semibold">Cue 1 — LX 01 — Otevření sálu</div>
            <div className="text-muted text-xs mt-1">departments: LX · SX · VIDEO &nbsp;·&nbsp; trigger: manual GO &nbsp;·&nbsp; pre-wait: 0 ms</div>
          </div>
          <div className="py-2 pl-4 border-b border-rule/20 space-y-0.5">
            <div className="flex gap-3">
              <span className="text-accent-deep w-20 shrink-0">lx_ref</span>
              <span className="text-muted">číslo cue: 0001 &nbsp;→ routing: lx_ref → <strong>lx_eos</strong> (ETC Eos)</span>
            </div>
            <div className="pl-20 text-muted text-xs">výsledný OSC: /eos/cue/1/0001/fire → 127.0.0.1:8000</div>
          </div>
          <div className="py-2 pl-4 border-b border-rule/20 space-y-0.5">
            <div className="flex gap-3">
              <span className="text-accent-deep w-20 shrink-0">osc</span>
              <span className="text-muted">cesta: /cue/1/start · args: [] &nbsp;→ routing: tag=SX → <strong>sx_qlab</strong></span>
            </div>
            <div className="pl-20 text-muted text-xs">výsledný OSC: /cue/1/start → 127.0.0.1:53000</div>
          </div>
          <div className="py-2 pl-4 space-y-0.5">
            <div className="flex gap-3">
              <span className="text-accent-deep w-20 shrink-0">webhook</span>
              <span className="text-muted">POST /play · body: {'{"action":"start"}'} &nbsp;→ routing: tag=VIDEO → <strong>video_disguise</strong></span>
            </div>
            <div className="pl-20 text-muted text-xs">HTTP POST → 127.0.0.1:9000/play</div>
          </div>
        </div>
      </div>
      <p>
        Operátor LX vidí na svojí stanici řádek "LX 01 — Otevření sálu" zvýrazněný jako LX cue. Operátor SX vidí
        stejnou cue jako SX cue. SM vidí všechny tři. Žádný z nich nevidí payloady ostatních oddělení — jen svůj výřez.
      </p>
      <p>
        Jeden GO od SM spustí všechny tři payloady paralelně. Dispatch Log v shellu zaznamená každý jako samostatný
        záznam s timestampem.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Triggery (kdy se cue spustí)</h3>
      <p>ShowX podporuje čtyři typy triggerů:</p>
      <ul>
        <li><strong>manual GO</strong> — SM stiskne GO button nebo klávesovou zkratku (výchozí)</li>
        <li><strong>auto-follow</strong> — spustí se automaticky po skončení předchozí cue (po uplynutí pre-wait)</li>
        <li><strong>auto-continue</strong> — jako follow, ale pokračuje bez čekání na konec předchozí</li>
        <li><strong>timecode</strong> — spustí se, když master clock dosáhne zadané pozice HH:MM:SS:FF (viz kapitola 6)</li>
        <li><strong>hotkey</strong> — přiřazená klávesa na klávesnici FOH Macu</li>
      </ul>
    </>
  )
}

function EnBody() {
  return (
    <>
      <p>
        The cue itself is just a named slot. All the "work" happens in payloads. ShowX supports eight payload types —
        one for each class of hardware or integration.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Payload type overview</h3>
      <div className="my-4 space-y-3 text-sm">
        {[
          {
            tag: 'lx_ref',
            title: 'Lighting console reference',
            desc: 'Most common LX payload. Sends a cue number to the lighting console (driver eos = ETC Eos format). Routing: lx_ref → device with driver=eos. Output: /eos/cue/{cuelist}/{number}/fire via OSC.',
          },
          {
            tag: 'osc',
            title: 'Open Sound Control',
            desc: 'UDP packet to device host:port. Payload holds an OSC path and arguments. Universal — for QLab, MA2/3, Disguise, GrandMA, custom OSC devices.',
          },
          {
            tag: 'midi',
            title: 'MIDI',
            desc: 'Note on/off or Control Change on a MIDI port. Channel 1–16, note/CC number 0–127, value 0–127. For hardware MIDI devices and legacy consoles.',
          },
          {
            tag: 'msc',
            title: 'MIDI Show Control',
            desc: 'Standardised show-control protocol over MIDI. Go/stop/resume/load commands with a cue number. For QLab (MSC mode), Eos (MSC mode), MA2.',
          },
          {
            tag: 'dmx',
            title: 'Direct DMX output',
            desc: 'Sets a single DMX channel value (0–255) on a given universe. Via Art-Net (UDP port 6454) or sACN (UDP port 5568). For simple LED/dimmer without a console.',
          },
          {
            tag: 'webhook',
            title: 'HTTP Webhook',
            desc: 'POST request to a given URL. Body is a JSON object. For systems with a REST API — media servers, custom control apps, IoT gateways.',
          },
          {
            tag: 'wait',
            title: 'Wait (pause)',
            desc: 'Inserts a pause in milliseconds. Used inside a group payload for sequential firing with time gaps.',
          },
          {
            tag: 'group',
            title: 'Payload group',
            desc: 'Nested set of payloads fired as one unit. Lets you mix types and insert wait pauses for complex timing.',
          },
        ].map(({ tag, title, desc }) => (
          <div key={tag} className="rounded border border-rule/40 p-4">
            <div className="flex gap-3 items-start">
              <code className="text-accent-deep font-bold w-20 shrink-0 font-mono">{tag}</code>
              <div>
                <div className="font-semibold text-ink">{title}</div>
                <div className="text-muted mt-1 leading-relaxed">{desc}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <h3 className="display-serif text-xl mt-10 mb-3">Values and units</h3>
      <ul>
        <li><strong>DMX value</strong>: 0–255 (8-bit). 0 = off, 255 = full intensity.</li>
        <li><strong>MIDI value (velocity / CC value)</strong>: 0–127 (7-bit). 127 = maximum.</li>
        <li><strong>MIDI channel</strong>: 1–16.</li>
        <li><strong>OSC numbers</strong>: float32 or int32 depending on the target — QLab prefers int32, Eos float.</li>
        <li><strong>DMX universe</strong>: 0–32767 (Art-Net) or 1–63999 (sACN).</li>
      </ul>

      <h3 className="display-serif text-xl mt-10 mb-3">Routing: three-layer model</h3>
      <p>A payload does not know the device IP directly. Three-layer routing separates concerns:</p>
      <ol>
        <li><strong>Payload</strong> — intent in show language: "this cue wants to hit the LX console" (lx_ref) or "send this to SX" (tag=SX).</li>
        <li><strong>Routing rule</strong> — match (<code>payload_type</code> or <code>tag_pattern</code>) → <code>target_device_id</code>. Configured in the Routing tab in the shell.</li>
        <li><strong>Device</strong> — the concrete device: IP, port, driver. Configured in the Devices tab.</li>
      </ol>
      <p>If you swap the LX console for a different model, you only change the driver and IP — cues stay untouched.</p>

      <h3 className="display-serif text-xl mt-10 mb-3">Example: multi-department cue</h3>
      <p>A real cue for a big show-opening moment:</p>
      <div className="my-6 rounded border border-rule/40 overflow-hidden font-mono text-sm">
        <div className="bg-ink/5 px-5 py-1">
          <div className="py-3 border-b border-rule/40">
            <div className="text-ink font-semibold">Cue 1 — LX 01 — Opening the hall</div>
            <div className="text-muted text-xs mt-1">departments: LX · SX · VIDEO &nbsp;·&nbsp; trigger: manual GO &nbsp;·&nbsp; pre-wait: 0 ms</div>
          </div>
          <div className="py-2 pl-4 border-b border-rule/20 space-y-0.5">
            <div className="flex gap-3">
              <span className="text-accent-deep w-20 shrink-0">lx_ref</span>
              <span className="text-muted">cue number: 0001 &nbsp;→ routing: lx_ref → <strong>lx_eos</strong> (ETC Eos)</span>
            </div>
            <div className="pl-20 text-muted text-xs">resulting OSC: /eos/cue/1/0001/fire → 127.0.0.1:8000</div>
          </div>
          <div className="py-2 pl-4 border-b border-rule/20 space-y-0.5">
            <div className="flex gap-3">
              <span className="text-accent-deep w-20 shrink-0">osc</span>
              <span className="text-muted">path: /cue/1/start · args: [] &nbsp;→ routing: tag=SX → <strong>sx_qlab</strong></span>
            </div>
            <div className="pl-20 text-muted text-xs">resulting OSC: /cue/1/start → 127.0.0.1:53000</div>
          </div>
          <div className="py-2 pl-4 space-y-0.5">
            <div className="flex gap-3">
              <span className="text-accent-deep w-20 shrink-0">webhook</span>
              <span className="text-muted">POST /play · body: {'{"action":"start"}'} &nbsp;→ routing: tag=VIDEO → <strong>video_disguise</strong></span>
            </div>
            <div className="pl-20 text-muted text-xs">HTTP POST → 127.0.0.1:9000/play</div>
          </div>
        </div>
      </div>
      <p>
        The LX operator sees "LX 01 — Opening the hall" on their station, highlighted as an LX cue. The SX operator
        sees the same cue as an SX cue. The SM sees all three. None of them see the other departments' payloads — just
        their slice.
      </p>
      <p>
        One GO from the SM fires all three payloads in parallel. The Dispatch Log in the shell records each as a
        separate entry with a timestamp.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Triggers (when does a cue fire)</h3>
      <p>ShowX supports five trigger types:</p>
      <ul>
        <li><strong>manual GO</strong> — SM presses GO button or keyboard shortcut (default)</li>
        <li><strong>auto-follow</strong> — fires automatically after the previous cue ends (after its pre-wait elapses)</li>
        <li><strong>auto-continue</strong> — like follow but continues without waiting for the previous cue to finish</li>
        <li><strong>timecode</strong> — fires when the master clock reaches a given HH:MM:SS:FF position (see chapter 6)</li>
        <li><strong>hotkey</strong> — an assigned key on the FOH Mac keyboard</li>
      </ul>
    </>
  )
}

export default function Chapter3({ cs }: Props) {
  return (
    <article id="kap-3" className="scroll-mt-24">
      <h2 className="display-serif text-display-2 leading-tight mb-8">
        {cs ? '3. Cues a payloady prakticky' : '3. Cues and payloads in practice'}
      </h2>
      <div className="copy prose-guide max-w-none">
        {cs ? <CsBody /> : <EnBody />}
      </div>
    </article>
  )
}
